import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';

// Material UI Icons
import DashboardIcon from '@mui/icons-material/Dashboard';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonIcon from '@mui/icons-material/Person';
import EditIcon from '@mui/icons-material/Edit';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import SettingsIcon from '@mui/icons-material/Settings';
import AssignmentIcon from '@mui/icons-material/Assignment';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';

import Loading from '../../../components/global/loading/Loading';
import { useProduct } from '../../../context/ProductContext';
import { Product } from '../../../models/Product';
import Content from '../content/Content';
import { useAuth } from '../../../context/AuthContext';
import AdminProductCard from '../../../components/AdminProductCard';

function MainPage() {
  const { user, tokenExpiration } = useAuth();
  const { getProductsWithMissingDeliveryCosts } = useProduct();

  const [products, setProducts] = useState<Product[] | null>(null);
  const [showAll, setShowAll] = useState(false);
  const [timeLeft, setTimeLeft] = useState('');

  // Obliczanie czasu pozostałego do wygaśnięcia sesji
  useEffect(() => {
    if (tokenExpiration) {
      const calculateTimeLeft = () => {
        const expirationTime = new Date(tokenExpiration).getTime();
        const now = new Date().getTime();
        const difference = expirationTime - now;

        if (difference > 0) {
          const hours = Math.floor(
            (difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
          );
          const minutes = Math.floor(
            (difference % (1000 * 60 * 60)) / (1000 * 60),
          );
          const seconds = Math.floor((difference % (1000 * 60)) / 1000);

          setTimeLeft(
            `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`,
          );
        } else {
          setTimeLeft('Sesja wygasła');
        }
      };

      calculateTimeLeft();
      const timer = setInterval(calculateTimeLeft, 1000);

      return () => clearInterval(timer);
    } else {
      setTimeLeft('Informacja niedostępna');
    }
  }, [tokenExpiration]);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const products = await getProductsWithMissingDeliveryCosts();
        setProducts(products);
      } catch (error) {
        toast.error('Nie udało się pobrać produktów');
        setProducts([]);
      }
    };

    fetchProducts();
  }, [getProductsWithMissingDeliveryCosts]);

  // Szybkie linki do panelu
  const quickLinks = [
    {
      icon: <ShoppingCartIcon />,
      title: 'Zamówienia',
      path: '/admin/zamówienia',
      desc: 'Zarządzaj zamówieniami klientów',
    },
    {
      icon: <AssignmentIcon />,
      title: 'Produkty',
      path: '/admin/produkty',
      desc: 'Dodawaj i edytuj produkty',
    },
    {
      icon: <SettingsIcon />,
      title: 'Ustawienia',
      path: '/admin/parameters',
      desc: 'Parametry i konfiguracja sklepu',
    },
    {
      icon: <LocalShippingIcon />,
      title: 'Dostawy',
      path: '/admin/produkty',
      desc: 'Koszty i metody dostawy',
    },
  ];

  return (
    <div className="space-y-8">
      {/* Header section */}
      <motion.div
        className="flex flex-col space-y-2"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="flex items-center gap-3">
          <DashboardIcon className="text-primary text-3xl" />
          <h1 className="text-3xl font-bold text-gray-800">Panel główny</h1>
        </div>
        <p className="text-gray-500 pl-1">
          Witaj w panelu administracyjnym, {user?.name}. Poniżej znajdziesz
          skróty do najważniejszych funkcji i alertów.
        </p>
      </motion.div>

      {/* Status cards */}
      <motion.div
        className="grid grid-cols-1 md:grid-cols-3 gap-5"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        {/* User status card */}
        <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm flex items-center gap-4">
          <div className="bg-blue-100 p-3 rounded-lg">
            <PersonIcon className="text-blue-600" />
          </div>
          <div>
            <div className="text-sm text-gray-500">Zalogowany jako</div>
            <div className="font-medium text-gray-800">{user?.email}</div>
            <div className="text-xs text-gray-500 mt-1">{user?.role}</div>
          </div>
        </div>

        {/* Session card */}
        <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm flex items-center gap-4">
          <div className="bg-amber-100 p-3 rounded-lg">
            <AccessTimeIcon className="text-amber-600" />
          </div>
          <div>
            <div className="text-sm text-gray-500">Sesja wygaśnie za</div>
            <div className="font-medium text-gray-800">{timeLeft}</div>
            <div className="text-xs text-gray-500 mt-1">
              {tokenExpiration
                ? new Date(tokenExpiration).toLocaleString('pl-PL')
                : 'Informacja niedostępna'}
            </div>
          </div>
        </div>

        {/* Alerts card */}
        <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm flex items-center gap-4">
          <div className="bg-red-100 p-3 rounded-lg">
            <WarningAmberIcon className="text-red-600" />
          </div>
          <div>
            <div className="text-sm text-gray-500">Alerty</div>
            <div className="font-medium text-gray-800">
              {products
                ? `${products.length} ${
                    products.length === 1
                      ? 'produkt'
                      : products.length > 1 && products.length < 5
                        ? 'produkty'
                        : 'produktów'
                  } wymaga uwagi`
                : 'Ładowanie alertów...'}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              Sprawdź szczegóły poniżej
            </div>
          </div>
        </div>
      </motion.div>

      {/* Quick links */}
      <motion.div
        className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.3 }}
      >
        <div className="border-b border-gray-200 px-6 py-4">
          <h2 className="text-lg font-semibold text-gray-800">Szybki dostęp</h2>
          <p className="text-sm text-gray-500">
            Skróty do najczęściej używanych funkcji
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
          {quickLinks.map((link, index) => (
            <Link
              key={index}
              to={link.path}
              className="border-b sm:border-r border-gray-200 p-5 hover:bg-gray-50 transition-colors group"
            >
              <div className="flex items-start gap-4">
                <div className="bg-gray-100 p-3 rounded-lg text-gray-600 group-hover:bg-primary/10 group-hover:text-primary transition-colors">
                  {link.icon}
                </div>
                <div>
                  <h3 className="font-medium text-gray-800 group-hover:text-primary transition-colors">
                    {link.title}
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">{link.desc}</p>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </motion.div>

      {/* Content editor section */}
      <motion.div
        className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.4 }}
      >
        <div className="border-b border-gray-200 px-6 py-4 flex justify-between items-center">
          <div>
            <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
              <EditIcon fontSize="small" className="text-primary" />
              Zawartość strony
            </h2>
            <p className="text-sm text-gray-500">
              Edytuj sekcje widoczne dla klientów
            </p>
          </div>
        </div>
        <div className="p-6">
          <Content />
        </div>
      </motion.div>

      {/* Products with missing delivery costs */}
      <motion.div
        className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.5 }}
      >
        <div className="border-b border-gray-200 px-6 py-4 flex justify-between items-center">
          <div>
            <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
              <LocalShippingIcon fontSize="small" className="text-primary" />
              Produkty z brakującymi kosztami dostawy
            </h2>
            <p className="text-sm text-gray-500">
              Dodaj koszty dostawy aby produkty były widoczne dla klientów
            </p>
          </div>
          {products && products.length > 3 && (
            <button
              onClick={() => setShowAll(!showAll)}
              className="text-primary hover:text-primary-dark text-sm font-medium flex items-center gap-1 transition-colors"
            >
              {showAll
                ? 'Pokaż mniej'
                : `Zobacz wszystkie (${products.length})`}
              <ArrowForwardIcon fontSize="small" />
            </button>
          )}
        </div>
        <div className="p-6">
          <AnimatePresence mode="wait">
            {products === null ? (
              <motion.div
                key="loading"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex justify-center py-8"
              >
                <Loading />
              </motion.div>
            ) : products.length === 0 ? (
              <motion.div
                key="empty"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="text-center py-8"
              >
                <div className="bg-green-50 text-green-600 p-3 rounded-full inline-flex mb-3">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-800">
                  Wszystkie produkty mają koszty dostawy
                </h3>
                <p className="text-gray-500 max-w-md mx-auto mt-1">
                  Wszystkie produkty mają poprawnie skonfigurowane koszty
                  dostawy i są widoczne dla klientów.
                </p>
              </motion.div>
            ) : (
              <motion.div
                key="products"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="space-y-3"
              >
                {(showAll ? products : products.slice(0, 3)).map(
                  (product, index) => (
                    <motion.div
                      key={product.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <AdminProductCard product={product} />
                    </motion.div>
                  ),
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
}

export default MainPage;
